import {
  AnalyticsPageName,
  BacktestingScoreBreakdown,
  LanguageNameSpaces,
  MarketplaceSubGroup,
  ScoreValue,
} from "@shared/types";
import { useTheme } from "styled-components";
import { useCallback, useMemo, useState } from "react";
import {
  getSumValues,
  roundScoreValues,
} from "@shared/features/newMarketplace/utils";
import { ScoreSection } from "@shared/features/newMarketplace/types";
import {
  KEYS_TO_DISPLAY_TEXT,
  SCORE_KEYS_TO_HELPER_PROPS,
  SCORE_SECTION_TO_COLOR,
  SCORE_SECTION_TO_DARK_MODE_COLOR,
  SCORE_SECTION_TO_KEYS,
} from "@shared/features/newMarketplace/constants";
import { toFixed } from "@shared/utils/numbers";
import { getDisplayTimeDuration } from "@shared/utils/date";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TranslationKeys } from "@shared/common/i18n/types";

interface useScoreProps {
  group?: MarketplaceSubGroup;
}

const useScore = ({ group }: useScoreProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const theme = useTheme();
  const isDarkMode = theme.isDarkMode;
  const scoreBreakdown = group?.backtesting_score_breakdown;
  const sections = Object.values(ScoreSection);

  const [expanded, setExpanded] = useState({
    [ScoreSection.PERFORMANCE]: false,
    [ScoreSection.RISK]: false,
    [ScoreSection.SERVICE]: false,
  });
  const [itemExpanded, setItemExpanded] = useState<{
    [key in keyof BacktestingScoreBreakdown]?: boolean;
  }>({});

  const sectionsKeysToSumValues = useMemo(() => {
    let sectionsKeysToSumValues = {};
    Object.entries(SCORE_SECTION_TO_KEYS).forEach(([section, keys]) => {
      let keysToSumWeight = {};
      keys.forEach((key) => {
        keysToSumWeight = {
          ...keysToSumWeight,
          [key]: getSumValues(scoreBreakdown, key),
        };
      });
      sectionsKeysToSumValues = {
        ...sectionsKeysToSumValues,
        [section]: keysToSumWeight,
      };
    });
    return sectionsKeysToSumValues as Record<
      ScoreSection,
      { [key in keyof BacktestingScoreBreakdown]?: ScoreValue }
    >;
  }, [scoreBreakdown]);

  const roundedSectionScores = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(SCORE_SECTION_TO_KEYS).map(([section, keys]) => {
          const roundedScores = roundScoreValues(
            keys.map(
              (key) =>
                sectionsKeysToSumValues[section as ScoreSection][key]
                  ?.weighted_score || 0
            )
          );
          return [
            section,
            Object.fromEntries(
              keys.map((key, index) => [key, roundedScores[index]])
            ),
          ];
        })
      ),
    [sectionsKeysToSumValues]
  );

  const onExpandCollapseClick = (section: ScoreSection) => {
    setExpanded((prev) => {
      return { ...prev, [section]: !prev[section] };
    });
  };

  const onExpandCollapseItemClick = (key: keyof BacktestingScoreBreakdown) => {
    setItemExpanded((prev) => {
      return { ...prev, [key]: !prev[key] };
    });
  };

  const getSumSectionByKey = useCallback(
    (section: ScoreSection, key: "weighted_score" | "weight") => {
      return Object.values(sectionsKeysToSumValues?.[section]).reduce(
        (sum: number, score) => sum + score[key],
        0
      );
    },
    [sectionsKeysToSumValues]
  );

  const getFormattedValue = (
    key: keyof BacktestingScoreBreakdown,
    value: number
  ) => {
    if (key === "pnl" || key === "max_drawdown" || key === "has_stop_loss") {
      return `${toFixed(value * 100, 1)}%`;
    } else if (
      key === "beats_the_market" ||
      key === "is_free_or_has_free_trial"
    ) {
      return value === 1 ? t("score.values.yes") : t("score.values.no");
    } else if (key === "winning_losing" || key === "pnl_standard_deviation") {
      return `${Math.round(value * 100)}%`;
    } else if (key === "avg_rating") {
      if (value === 0) return t("score.values.not_enough_reviews");
      return toFixed(value, 1);
    } else if (key === "low_leverage") {
      return `${toFixed(value, 1)}x`;
    } else if (key === "avg_trade_time" || key === "age") {
      return getDisplayTimeDuration(value);
    } else if (key === "sharpe_ratio" || key === "sortino_ratio") {
      return toFixed(value, 2);
    } else {
      return value;
    }
  };

  const getSectionData = (section: ScoreSection, index: number) => {
    return {
      mainContent: SCORE_SECTION_TO_KEYS[section].slice(0, 2),
      collapsedContent: SCORE_SECTION_TO_KEYS[section].slice(2),
      isLastSection: index === sections.length - 1,
      onClick: () => onExpandCollapseClick(section),
      expanded: expanded[section],
    };
  };

  const getSectionRowData = (section: ScoreSection) => {
    const weightedScore = Math.round(
      getSumSectionByKey(section, "weighted_score")
    );
    const weight = Math.round(getSumSectionByKey(section, "weight"));
    return {
      weightedScore,
      weight,
      progressPercent: weight ? (weightedScore / weight) * 100 : 0,
      progressColor: isDarkMode
        ? SCORE_SECTION_TO_DARK_MODE_COLOR[section]
        : SCORE_SECTION_TO_COLOR[section],
    };
  };

  const getProgressBarData = (
    key: keyof BacktestingScoreBreakdown,
    section: ScoreSection
  ) => {
    const sumValues = sectionsKeysToSumValues?.[section]?.[key];
    const isCollapsible = Object.keys(scoreBreakdown?.[key] || {}).length > 1;
    const weightedScore = roundedSectionScores[section][key] || 0;
    const weight = Math.round(sumValues?.weight || 0);
    return {
      title: t(KEYS_TO_DISPLAY_TEXT[key]),
      value: getFormattedValue(key, sumValues?.value || 0),
      isCollapsible,
      onClick: () => onExpandCollapseItemClick(key),
      weightedScore,
      weight,
      expanded: itemExpanded[key],
      progressPercent: weight ? (weightedScore / weight) * 100 : 0,
      progressColor: isDarkMode
        ? SCORE_SECTION_TO_DARK_MODE_COLOR[section]
        : SCORE_SECTION_TO_COLOR[section],
      helperProps: getHelperPropsByKey(key),
    };
  };

  const getTimeframeScoreData = (
    key: keyof BacktestingScoreBreakdown,
    section: ScoreSection
  ) => {
    const timeframes = Object.entries(scoreBreakdown?.[key] || {});
    const roundedScores = roundScoreValues(
      timeframes.map(([, score]) => score.weighted_score * 100),
      roundedSectionScores[section][key] || 0
    );
    return timeframes.map(([timeframe, score], index) => ({
      timeframe,
      value: score.value,
      weightedScore: roundedScores[index],
      weight: Math.round(score.weight * 100),
    }));
  };

  const getHelperPropsByKey = (key: keyof BacktestingScoreBreakdown) => {
    const helperProps = SCORE_KEYS_TO_HELPER_PROPS[key];
    if (helperProps) {
      return {
        analyticsPageName: AnalyticsPageName.MARKETPLACE,
        analyticsFieldName: t(KEYS_TO_DISPLAY_TEXT[key]),
        description: t(
          helperProps.description as TranslationKeys<LanguageNameSpaces.MARKETPLACE>
        ),
        articleId: helperProps.articleId,
      };
    }
  };

  return {
    theme,
    scoreBreakdown,
    sections,
    getSumSectionByKey,
    getFormattedValue,
    getSectionData,
    getSectionRowData,
    getProgressBarData,
    getTimeframeScoreData,
  };
};

export default useScore;
